import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Box, IconButton, SxProps, Theme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

type HorizontalOverflowScrollerProps = {
  children: React.ReactNode;
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  sx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
  fadeColor?: string;
  fadeWidth?: number;
  ariaLabel?: string;
  showArrows?: boolean;
  onEndReached?: () => void;
  onEndReachedThreshold?: number;
};

const EDGE_EPSILON = 6;

const HorizontalOverflowScroller: React.FC<HorizontalOverflowScrollerProps> = ({
  children,
  scrollRef,
  sx,
  contentSx,
  fadeColor = '#101219',
  fadeWidth = 34,
  ariaLabel,
  showArrows = false,
  onEndReached,
  onEndReachedThreshold = 180,
}) => {
  const internalScrollRef = useRef<HTMLDivElement>(null);
  const scrollerRef = scrollRef || internalScrollRef;
  const contentRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const updateEdges = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const distanceFromEnd = maxScrollLeft - scroller.scrollLeft;
    const nextEdges = {
      left: maxScrollLeft > EDGE_EPSILON && scroller.scrollLeft > EDGE_EPSILON,
      right: maxScrollLeft > EDGE_EPSILON && scroller.scrollLeft < maxScrollLeft - EDGE_EPSILON,
    };

    setEdges((current) => (
      current.left === nextEdges.left && current.right === nextEdges.right
        ? current
        : nextEdges
    ));
    if (onEndReached && distanceFromEnd <= onEndReachedThreshold) {
      onEndReached();
    }
  }, [onEndReached, onEndReachedThreshold, scrollerRef]);

  const scrollByDirection = useCallback((direction: -1 | 1) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollBy({
      left: direction * Math.max(180, scroller.clientWidth * 0.72),
      behavior: 'smooth',
    });
  }, [scrollerRef]);

  useLayoutEffect(() => {
    updateEdges();

    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(updateEdges);
    if (resizeObserver) {
      if (scrollerRef.current) resizeObserver.observe(scrollerRef.current);
      if (contentRef.current) resizeObserver.observe(contentRef.current);
    }

    window.addEventListener('resize', updateEdges);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateEdges);
    };
  }, [updateEdges]);

  const fadeSx = {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: fadeWidth,
    zIndex: 2,
    pointerEvents: 'none',
  } as const;

  const arrowSx = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 3,
    width: 32,
    height: 32,
    bgcolor: 'rgba(14,16,22,0.78)',
    color: 'rgba(255,255,255,0.76)',
    border: '1px solid rgba(255,255,255,0.15)',
    boxShadow: '0 2px 9px rgba(0,0,0,0.26)',
    transition: 'opacity 150ms ease, background-color 150ms ease, color 150ms ease, box-shadow 150ms ease',
    '&:hover, &.Mui-focusVisible': {
      bgcolor: '#fff',
      color: '#0e0c0d',
      boxShadow: '0 3px 12px rgba(0,0,0,0.38)',
    },
  } as const;

  return (
    <Box
      sx={[
        {
          position: 'relative',
          minWidth: 0,
          '& .overflow-scroll-arrow': {
            opacity: 0,
            pointerEvents: 'none',
          },
          '&:hover .overflow-scroll-arrow, &:focus-within .overflow-scroll-arrow': {
            opacity: 1,
            pointerEvents: 'auto',
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        ref={scrollerRef}
        onScroll={updateEdges}
        aria-label={ariaLabel}
        sx={{
          minWidth: 0,
          width: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <Box
          ref={contentRef}
          sx={[
            {
              display: 'flex',
              alignItems: 'center',
              width: 'max-content',
              minWidth: '100%',
              boxSizing: 'border-box',
            },
            ...(Array.isArray(contentSx) ? contentSx : [contentSx]),
          ]}
        >
          {children}
        </Box>
      </Box>

      {edges.left && (
        <Box
          aria-hidden="true"
          sx={{
            ...fadeSx,
            left: 0,
            background: `linear-gradient(90deg, ${fadeColor} 10%, transparent 100%)`,
          }}
        />
      )}
      {edges.right && (
        <Box
          aria-hidden="true"
          sx={{
            ...fadeSx,
            right: 0,
            background: `linear-gradient(270deg, ${fadeColor} 10%, transparent 100%)`,
          }}
        />
      )}
      {showArrows && edges.left && (
        <IconButton
          className="overflow-scroll-arrow"
          type="button"
          aria-label={`Scroll ${ariaLabel || 'options'} left`}
          onClick={() => scrollByDirection(-1)}
          sx={{ ...arrowSx, left: 2 }}
        >
          <ChevronLeftIcon sx={{ fontSize: 23 }} />
        </IconButton>
      )}
      {showArrows && edges.right && (
        <IconButton
          className="overflow-scroll-arrow"
          type="button"
          aria-label={`Scroll ${ariaLabel || 'options'} right`}
          onClick={() => scrollByDirection(1)}
          sx={{ ...arrowSx, right: 2 }}
        >
          <ChevronRightIcon sx={{ fontSize: 23 }} />
        </IconButton>
      )}
    </Box>
  );
};

export default HorizontalOverflowScroller;
